import React, { useState, useEffect } from 'react';
import logo from '../Assets/logo.png';

const INSTITUTION_ID = 58;

const AdmissionForm = () => {
  const [formData, setFormData] = useState({});
  const [formFields, setFormFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [standards, setStandards] = useState([]);
  const [courses, setCourses] = useState([]);
  const [coursesType, setCoursesType] = useState(null);
  const [yearId, setYearId] = useState(null);
  const [institutionType, setInstitutionType] = useState(null);
  const [institutionName, setInstitutionName] = useState('');
  const [selectedStandard, setSelectedStandard] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [alert, setAlert] = useState({ message: '', type: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    fetchFormConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFormConfig = async () => {
    try {
      const institutionRes = await fetch('https://api.sirajulhuda.com/api/v1/erp/admission/admission-open/institutions');
      const institutionResult = await institutionRes.json();

      if (institutionResult.status === 200) {
        const institution = institutionResult.data.find(inst => inst.id === INSTITUTION_ID);
        if (institution) {
          setInstitutionName(institution.name);
        }
      }

      const response = await fetch(`https://api.sirajulhuda.com/api/v1/erp/admission-application-config/${INSTITUTION_ID}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      const res = await response.json();
      const institutionFormDetails = res.data;
      const institutionFormConfigs = institutionFormDetails.config;

      const institutionTypeField = institutionFormConfigs.find(config => config.column_name === 'type');
      const yearIdField = institutionFormConfigs.find(config => config.column_name === 'year_id');
      const courseIdField = institutionFormConfigs.find(config => config.column_name === 'course_id');

      const courseValue = courseIdField ? courseIdField.type : null;
      const typeValue = institutionTypeField ? institutionTypeField.default_value : null;
      const yearValue = yearIdField ? yearIdField.default_value : null;

      setInstitutionType(typeValue);
      setCoursesType(courseValue);
      setYearId(yearValue);

      const fieldTypes = {
        str: 'text', standard: 'text', school: 'text',
        ration_card_type: 'text', yes_no: 'select',
        local_body: 'text', int: 'number', date: 'date'
      };

      const numberFields = [
        'mobile', 'whats_app', 'aadhaar', 'phone',
        'father_phone', 'mother_phone', 'pin_code'
      ];

      const updatedFormFields = institutionFormConfigs
        .filter(config =>
          config.visibility === true &&
          !config.title.toLowerCase().includes('standard') &&
          !config.title.toLowerCase().includes('course')
        )
        .map(config => ({
          name: config.column_name,
          title: config.title,
          type: numberFields.includes(config.column_name) ? 'number' : fieldTypes[config.type] || 'text',
          required: config.is_required,
          defaultValue: config.default_value,
          isYesNo: config.type === 'yes_no'
        }));

      setFormFields(updatedFormFields);

      const initialFormData = {};
      updatedFormFields.forEach(field => {
        if (field.defaultValue !== null) {
          initialFormData[field.name] = field.isYesNo
            ? (field.defaultValue ? "yes" : "no")
            : field.defaultValue;
        }
      });

      if (courseValue !== null) {
        await fetchCourses();
      } else {
        await fetchStandards();
      }

      setFormData(initialFormData);
    } catch (error) {
      console.error("Error fetching form config:", error);
      setAlert({ message: "Error loading form. Please refresh the page.", type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchStandards = async () => {
    try {
      const response = await fetch(`https://api.sirajulhuda.com/api/v1/erp/school/standards/list/${INSTITUTION_ID}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" }
      });
      const result = await response.json();
      setStandards(result.data || []);
    } catch (error) {
      console.error("Error fetching standards:", error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await fetch(`https://api.sirajulhuda.com/api/v1/erp/list-institution-courses?institution_id=${INSTITUTION_ID}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" }
      });
      const result = await response.json();
      setCourses(result.data || []);
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setAlert({ message: '', type: '' });

    const submissionData = {
      institution_id: INSTITUTION_ID,
      standard: selectedStandard,
      course_id: selectedCourse,
      year_id: yearId,
      type: institutionType,
      ...formData
    };

    try {
      const response = await fetch('https://api.sirajulhuda.com/api/v1/erp/admission/application/create', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          'institution_id': Number(INSTITUTION_ID),
          'standard_id': submissionData.standard === null || submissionData.standard === '' ? null : Number(submissionData.standard),
          'course_id': submissionData.course_id === null || submissionData.course_id === '' ? null : Number(submissionData.course_id),
          'year_id': submissionData.year_id === null ? null : Number(submissionData.year_id),
          'type': submissionData.type === null ? null : submissionData.type,
          'mobile': submissionData.mobile,
          'whats_app': submissionData.whats_app || null,
          'email': submissionData.email || null,
          'name': submissionData.name || null,
          'full_initial': submissionData.full_initial || null,
          'dob': submissionData.dob ? new Date(submissionData.dob).toISOString().slice(0, 19).replace('T', ' ') : null,
          'word_dob': submissionData.word_dob || null,
          'gender': submissionData.gender || null,
          'blood_group': submissionData.blood_group || null,
          'religion': submissionData.religion || null,
          'caste': submissionData.caste || null,
          'aadhaar': submissionData.aadhaar || null,
          'mother_tongue': submissionData.mother_tongue || null,
          'house_name': submissionData.house_name || null,
          'local_name': submissionData.local_name || null,
          'local_body': submissionData.local_body || null,
          'ration_card_type': submissionData.ration_card_type || null,
          'pin_code': submissionData.pin_code || null,
          'post': submissionData.post || null,
          'place': submissionData.place || null,
          'block': submissionData.block || null,
          'taluk': submissionData.taluk || null,
          'district': submissionData.district || null,
          'state': submissionData.state || null,
          'nationality': submissionData.nationality || null,
          'is_single_girl': true,
          'is_disabled': submissionData.is_disabled || null,
          'father_name': submissionData.father_name || null,
          'father_phone': submissionData.father_phone || null,
          'father_income': submissionData.father_income || null,
          'father_email': submissionData.father_email || null,
          'father_qualification': submissionData.father_qualification || null,
          'father_occupation': submissionData.father_occupation || null,
          'mother_name': submissionData.mother_name || null,
          'mother_occupation': submissionData.mother_occupation || null,
          'mother_email': submissionData.mother_email || null,
          'mother_phone': submissionData.mother_phone || null,
          'mother_qualification': submissionData.mother_qualification || null,
          'mother_income': submissionData.mother_income || null,
          'guardian': submissionData.guardian || null,
          'relationship': submissionData.relationship || null,
          'guardian_address': submissionData.guardian_address || null,
          'last_school': submissionData.last_school || null,
          'last_school_affiliation': submissionData.last_school_affiliation || null,
          'tc_no': submissionData.tc_no || null,
          'tc_issue_date': submissionData.tc_issue_date || null,
          'id_mark': submissionData.id_mark || null,
          'father_job_title': submissionData.father_job_title || null,
        }),
      });

      const result = await response.json();

      if (result.detail === "application existed") {
        setAlert({ message: 'Application already exists with this mobile number!', type: 'error' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setShowSuccessModal(true);
      }

    } catch (error) {
      console.error("Error submitting form:", error);
      setAlert({ message: 'Error submitting application. Please try again.', type: 'error' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setFormData({});
    setSelectedStandard('');
    setSelectedCourse('');
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="loading-content">
          <div className="loading-spinner-large"></div>
          <p style={{ color: '#5C5552', fontFamily: 'Poppins, sans-serif' }}>Loading form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admission-page">
      <div className="form-container">
        <div className="form-card">
          <div className="logo-container">
            <img src={logo} className="logo-img" alt="La Fille Logo" />
            <p className="school-name">Global Girls Secondary School</p>
          </div>

          <h1 className="form-title">Admission Application</h1>
          <p className="form-subtitle">Please fill in all required details carefully</p>

          <div className="instructions-box">
            <div className="instruction-item">
              <span className="instruction-number">1</span>
              <span>Fill the form using CAPITAL LETTERS only</span>
            </div>
            <div className="instruction-item">
              <span className="instruction-number">2</span>
              <span>Fields marked with red asterisk (*) are mandatory</span>
            </div>
            <div className="instruction-item">
              <span className="instruction-number">3</span>
              <span>Verify your phone numbers before submission</span>
            </div>
            <div className="instruction-item">
              <span className="instruction-number">4</span>
              <span>Submit the form after completing all fields</span>
            </div>
          </div>

          {alert.message && (
            <div className={`alert ${alert.type === 'error' ? 'alert-error' : 'alert-success'}`}>
              {alert.type === 'error' ? (
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
              {alert.message}
            </div>
          )}

          {institutionName && (
            <div className="institution-info">
              <p className="institution-label">Institution</p>
              <p className="institution-name">{institutionName}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {coursesType !== null ? (
              <div className="form-group">
                <label htmlFor="course" className="form-label">
                  Select Course<span className="required">*</span>
                </label>
                <select
                  id="course"
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Choose a Course</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="standard" className="form-label">
                  Select Class/Standard<span className="required">*</span>
                </label>
                <select
                  id="standard"
                  value={selectedStandard}
                  onChange={(e) => setSelectedStandard(e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Choose a Standard</option>
                  {standards.map((std) => (
                    <option key={std.id} value={std.id}>
                      {std.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-grid">
              {formFields.map((field) => (
                <div key={field.name} className="form-group">
                  <label htmlFor={field.name} className="form-label">
                    {field.title}
                    {field.required && <span className="required">*</span>}
                  </label>
                  {field.isYesNo ? (
                    <select
                      id={field.name}
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={handleChange}
                      required={field.required}
                      className="form-select"
                    >
                      <option value="">Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  ) : (
                    <input
                      type={field.type}
                      id={field.name}
                      name={field.name}
                      required={field.required}
                      value={formData[field.name] || ''}
                      onChange={handleChange}
                      className="form-input"
                      placeholder={`Enter ${field.title}`}
                    />
                  )}
                </div>
              ))}
            </div>

            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="loading-spinner"></span>
                  Submitting...
                </>
              ) : (
                <>
                  Submit Application
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {showSuccessModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="success-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="modal-title">Application Submitted!</h2>
            <p className="modal-message">
              Your admission application has been submitted successfully.
              We will review your application and contact you soon.
            </p>
            <button className="modal-btn" onClick={handleCloseModal}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdmissionForm;
